/**
 * IndexedDB plumbing for the browser implementations of the asset store, the play
 * event queue and the persisted player state. One database, four stores, so a power
 * cut leaves a single consistent thing to reopen.
 *
 * The native shell mirrors the event queue over its JS bridge (technical-design
 * §11.6); that mirror is dwf-032's side of the bridge and is wired in shell.ts.
 */

export const DATABASE_NAME = 'dw-player';
export const DATABASE_VERSION = 1;

export const STORE_ASSETS = 'assets';
export const STORE_PARTIALS = 'partials';
export const STORE_EVENTS = 'events';
export const STORE_STATE = 'state';

export function openPlayerDatabase(factory: IDBFactory = indexedDB): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_ASSETS)) {
        db.createObjectStore(STORE_ASSETS, { keyPath: 'sha256' });
      }
      if (!db.objectStoreNames.contains(STORE_PARTIALS)) {
        db.createObjectStore(STORE_PARTIALS, { keyPath: 'sha256' });
      }
      if (!db.objectStoreNames.contains(STORE_EVENTS)) {
        db.createObjectStore(STORE_EVENTS, { keyPath: 'seq', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE_STATE)) {
        db.createObjectStore(STORE_STATE, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('indexeddb open failed'));
  });
}

export function toPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('indexeddb request failed'));
  });
}

export async function withStore<T>(
  db: IDBDatabase,
  storeName: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const transaction = db.transaction(storeName, mode);
  const store = transaction.objectStore(storeName);
  const result = await work(store);
  await new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error('indexeddb transaction aborted'));
    transaction.onerror = () => reject(transaction.error ?? new Error('indexeddb transaction failed'));
  });
  return result;
}
